/**
 * The men inside a vehicle (ROUND4 §1). While they ride they are not in `world.enemies` at all - the
 * hull carries a count - so nobody sees them, hears them or shoots them. They get down when the vehicle
 * reaches its dismount point, when it is stopped dead, or when it is wrecked: and a wreck takes some of
 * them with it, while the rest crawl out of it hurt.
 *
 * Kept apart from `vehicle.ts` for the file limit, and because this half is the only part that makes
 * soldiers: it is the one that imports `createEnemy`.
 */

import { sendToInvestigate } from '../../alert';
import type { Ctx } from '../../ctx';
import { cos, sin, TAU } from '../../math';
import { resolveMovement } from '../../statics';
import { ENEMY, secs, VEHICLES } from '../../tuning';
import type { Enemy, VehicleState } from '../../types';
import { createEnemy } from '../create';

/** Where the man in seat `i` of `count` puts his boots down: a ring round the hull, clear of it. */
function seat(ctx: Ctx, e: Enemy, i: number, count: number): { x: number; z: number } {
  const a = e.facing + TAU * ((i + 0.5) / Math.max(1, count));
  const ring = VEHICLES.dismountRing;
  return resolveMovement(ctx.statics, e.x + sin(a) * ring, e.z + cos(a) * ring, ENEMY.radius);
}

function putDown(ctx: Ctx, e: Enemy, v: VehicleState, i: number, count: number, hurt: boolean): Enemy {
  const w = ctx.world;
  const at = seat(ctx, e, i, count);
  // The man in the first seat of a vehicle that carries THE MAN is him, and the mission is about him from now on.
  const isTheMan = v.targetAboard && i === 0;
  const man = createEnemy(
    w.nextId++,
    { kind: isTheMan ? 'officer' : v.aboardKind, x: at.x, z: at.z, facing: e.facing },
    w.enemies.length,
    ctx.rng.float(),
    ctx.rng.float(),
    ctx.statics.def.threat,
  );
  if (isTheMan) {
    w.objective.targetEnemyId = man.id;
    w.objective.targetAboardId = -1;
  }
  // He belongs to whatever put the vehicle on the map, so the charges that wait for that column count him too -
  // and, whether that was a timed event or the lorry itself, his own driver does not then run him over.
  man.wokenBy = e.wokenBy ?? `aboard-${e.id}`;
  man.wary = true;
  if (hurt) {
    man.state = 'wounded';
    man.hp = 0;
    man.stateTicks = secs(1.5);
  } else {
    // Men do not get out of a lorry in the middle of a job for nothing: they look for whoever stopped it.
    sendToInvestigate(man, w.disturbanceX, w.disturbanceZ, true);
  }
  w.enemies.push(man);
  return man;
}

/** The men get down and fight: the vehicle has stopped, or reached the point it was taking them to. */
export function dismountAll(ctx: Ctx, e: Enemy, v: VehicleState): void {
  const count = v.aboard;
  v.aboard = 0;
  for (let i = 0; i < count; i++) putDown(ctx, e, v, i, count, false);
  ctx.events.push({ type: 'vehicle', id: e.id, kind: e.kind, what: 'dismount', count, x: e.x, z: e.z });
}

/**
 * A wrecked vehicle, reckoned once: half of those still aboard go up with it (they are bodies in the
 * hulk, which the debrief pays for like any other casualty), the rest bail out wounded. The hulk itself
 * now blocks the road, so the navigation grid is rebuilt round it.
 */
export function reckonWreck(ctx: Ctx, e: Enemy, v: VehicleState): void {
  v.wrecked = true;
  ctx.staticsDirty = true;
  const count = v.aboard;
  v.aboard = 0;
  const killed = Math.round(count * VEHICLES.killedAboard);
  for (let i = 0; i < count; i++) {
    const man = putDown(ctx, e, v, i, count, i >= killed);
    if (i >= killed) continue;
    man.state = 'dead';
    man.hp = 0;
    man.stateTicks = 0;
    // Nobody "discovers" a body in a burnt-out lorry: the wreck is announcement enough.
    man.found = true;
  }
  ctx.events.push({ type: 'vehicle', id: e.id, kind: e.kind, what: 'wrecked', count, x: e.x, z: e.z });
}

/** Every wreck nobody has reckoned yet. Runs with the hazards, so it reaches the dead as well as the living. */
export function stepVehicleWrecks(ctx: Ctx): void {
  for (const e of ctx.world.enemies) {
    const v = e.vehicle;
    if (!v || v.wrecked || e.state !== 'dead') continue;
    reckonWreck(ctx, e, v);
  }
}

/**
 * Vehicles that have driven off the end of their run leave the map, exactly as a scrambled jet does:
 * they are taken out of `world.enemies`, with an event, instead of standing there for ever.
 */
export function sweepDeparted(ctx: Ctx): void {
  const w = ctx.world;
  if (!w.enemies.some((e) => e.vehicle?.gone === true)) return;
  for (const e of w.enemies) {
    const v = e.vehicle;
    if (v?.gone !== true) continue;
    ctx.events.push({ type: 'vehicle', id: e.id, kind: e.kind, what: 'left', count: v.aboard, x: e.x, z: e.z });
    if (w.runnerId === e.id) w.runnerId = -1;
  }
  w.enemies = w.enemies.filter((e) => e.vehicle?.gone !== true);
}
